import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ShieldCheck, Lock, ArrowLeft, CheckCircle2, AlertCircle, Search } from "lucide-react";
import { api } from "../lib/api";

const CATEGORIES = [
  { value: "harassment", label: "Molestie / discriminazione" },
  { value: "corruption", label: "Corruzione / frode" },
  { value: "safety", label: "Sicurezza / salute" },
  { value: "financial", label: "Irregolarità finanziarie" },
  { value: "other", label: "Altro" },
];

export default function WhistleblowingPublic() {
  const [mode, setMode] = useState("submit"); // "submit" | "track"
  const [companyId, setCompanyId] = useState("");
  const [category, setCategory] = useState("safety");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [trackingCode, setTrackingCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lookupCode, setLookupCode] = useState("");
  const [lookupResult, setLookupResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!companyId.trim() || !subject.trim() || !body.trim()) {
      setError("Compila tutti i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/whistleblowing/submit", {
        company_id: companyId.trim(),
        category,
        subject: subject.trim(),
        body: body.trim(),
        contact_email: contactEmail.trim() || null,
      });
      setTrackingCode(r.data.tracking_code);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore invio segnalazione");
    } finally {
      setLoading(false);
    }
  };

  const lookup = async (e) => {
    e.preventDefault();
    setError("");
    setLookupResult(null);
    if (!lookupCode.trim()) return;
    try {
      const r = await api.get(`/whistleblowing/track/${lookupCode.trim()}`);
      setLookupResult(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Codice non valido");
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #0F172A 0%, #1E1B4B 100%)" }} data-testid="whistleblowing-public">
      <div className="max-w-[720px] mx-auto px-5 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm mb-8" data-testid="back-home">
          <ArrowLeft className="w-4 h-4" /> Torna alla home
        </Link>

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10.5px] font-bold tracking-[0.18em] uppercase mb-5"
               style={{ background: "rgba(139,92,246,0.15)", color: "#C4B5FD", border: "1px solid rgba(139,92,246,0.30)" }}>
            <Lock className="w-3 h-3" /> Canale anonimo crittografato
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight">Whistleblowing GDPR</h1>
          <p className="mt-4 text-white/70 max-w-[520px] mx-auto">
            Segnala in modo riservato comportamenti illeciti o irregolarità nella tua azienda. Conforme al D.lgs 24/2023.
          </p>
        </div>

        {/* Mode switch */}
        <div className="flex gap-2 mb-6 justify-center" data-testid="whistleblowing-tabs">
          <button onClick={() => { setMode("submit"); setError(""); }} data-testid="tab-submit"
            className="px-5 h-10 rounded-full text-sm font-semibold transition-all"
            style={{
              background: mode === "submit" ? "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)" : "rgba(255,255,255,0.06)",
              color: "#FFFFFF", border: mode === "submit" ? "none" : "1px solid rgba(255,255,255,0.12)",
            }}>
            Nuova segnalazione
          </button>
          <button onClick={() => { setMode("track"); setError(""); }} data-testid="tab-track"
            className="px-5 h-10 rounded-full text-sm font-semibold transition-all"
            style={{
              background: mode === "track" ? "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)" : "rgba(255,255,255,0.06)",
              color: "#FFFFFF", border: mode === "track" ? "none" : "1px solid rgba(255,255,255,0.12)",
            }}>
            Controlla stato
          </button>
        </div>

        <div className="rounded-3xl p-7 md:p-9" style={{
          background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.10)",
          backdropFilter: "blur(20px)",
        }}>
          {error && (
            <div className="mb-5 px-4 py-3 rounded-xl flex items-start gap-2 text-sm" style={{ background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.30)", color: "#FCA5A5" }}>
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{error}
            </div>
          )}

          {mode === "submit" && !trackingCode && (
            <form onSubmit={submit} className="space-y-4" data-testid="submit-form">
              <div>
                <label className="block text-xs font-bold tracking-wide uppercase mb-2 text-white/70">Company ID *</label>
                <input value={companyId} onChange={e => setCompanyId(e.target.value)} required data-testid="input-company-id"
                  placeholder="UUID dell'azienda (chiedi al rappresentante interno)"
                  className="w-full px-4 h-11 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-purple-400 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold tracking-wide uppercase mb-2 text-white/70">Categoria *</label>
                <select value={category} onChange={e => setCategory(e.target.value)} data-testid="select-category"
                  className="w-full px-4 h-11 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-purple-400 transition-all">
                  {CATEGORIES.map(c => <option key={c.value} value={c.value} style={{ background: "#1F1B4B" }}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold tracking-wide uppercase mb-2 text-white/70">Oggetto *</label>
                <input value={subject} onChange={e => setSubject(e.target.value)} required maxLength={200} data-testid="input-subject"
                  placeholder="Breve titolo della segnalazione"
                  className="w-full px-4 h-11 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-purple-400 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold tracking-wide uppercase mb-2 text-white/70">Descrizione *</label>
                <textarea value={body} onChange={e => setBody(e.target.value)} required rows={6} maxLength={3000} data-testid="input-body"
                  placeholder="Descrivi i fatti in modo chiaro e dettagliato. La tua identità non sarà collegata a questo messaggio."
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-purple-400 transition-all resize-none" />
              </div>
              <div>
                <label className="block text-xs font-bold tracking-wide uppercase mb-2 text-white/70">Email contatto (opzionale)</label>
                <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} data-testid="input-contact-email"
                  placeholder="Solo se vuoi ricevere risposta"
                  className="w-full px-4 h-11 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-purple-400 transition-all" />
              </div>

              <div className="text-[12px] text-white/60 px-4 py-3 rounded-xl bg-white/5">
                🔒 La segnalazione viene crittografata con Fernet (AES-128) prima del salvataggio. Solo l'admin principale dell'azienda può decifrarla.
              </div>

              <Button type="submit" disabled={loading} data-testid="btn-submit-report"
                className="w-full h-12 rounded-full font-semibold text-white border-0"
                style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)" }}>
                {loading ? "Invio in corso…" : "Invia segnalazione anonima"}
              </Button>
            </form>
          )}

          {mode === "submit" && trackingCode && (
            <div className="text-center py-6" data-testid="submit-success">
              <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400 mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Segnalazione ricevuta</h2>
              <p className="text-white/70 mb-6">Conserva questo codice per consultare lo stato:</p>
              <div className="font-mono text-2xl font-bold text-white px-6 py-4 rounded-2xl inline-block"
                   style={{ background: "rgba(139,92,246,0.15)", border: "2px dashed rgba(139,92,246,0.50)" }}
                   data-testid="tracking-code">
                {trackingCode}
              </div>
              <p className="text-white/50 text-xs mt-6">Il codice è l'unica chiave per tracciare lo stato. Non condividerlo.</p>
              <Button onClick={() => { setTrackingCode(null); setSubject(""); setBody(""); setContactEmail(""); }}
                className="mt-6" variant="outline" data-testid="btn-new-report">
                Nuova segnalazione
              </Button>
            </div>
          )}

          {mode === "track" && (
            <form onSubmit={lookup} className="space-y-4" data-testid="track-form">
              <div>
                <label className="block text-xs font-bold tracking-wide uppercase mb-2 text-white/70">Codice tracking</label>
                <input value={lookupCode} onChange={e => setLookupCode(e.target.value.toUpperCase())} required data-testid="input-tracking-code"
                  placeholder="WB-XXXXXXXXXX"
                  className="w-full px-4 h-11 rounded-xl bg-white/5 border border-white/10 text-white font-mono placeholder-white/30 focus:outline-none focus:border-purple-400 transition-all" />
              </div>
              <Button type="submit" data-testid="btn-track"
                className="w-full h-12 rounded-full font-semibold text-white border-0"
                style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)" }}>
                <Search className="w-4 h-4 mr-2" /> Verifica stato
              </Button>
              {lookupResult && (
                <div className="mt-5 p-5 rounded-2xl space-y-2" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.12)" }} data-testid="track-result">
                  <div className="flex justify-between"><span className="text-white/60 text-xs uppercase tracking-wide">Codice</span><span className="text-white font-mono">{lookupResult.tracking_code}</span></div>
                  <div className="flex justify-between"><span className="text-white/60 text-xs uppercase tracking-wide">Stato</span><span className="text-white font-bold">{lookupResult.status}</span></div>
                  <div className="flex justify-between"><span className="text-white/60 text-xs uppercase tracking-wide">Categoria</span><span className="text-white">{lookupResult.category}</span></div>
                  <div className="flex justify-between"><span className="text-white/60 text-xs uppercase tracking-wide">Inviata</span><span className="text-white text-sm">{new Date(lookupResult.submitted_at).toLocaleString("it-IT")}</span></div>
                </div>
              )}
            </form>
          )}
        </div>

        <div className="mt-8 text-center text-xs text-white/50">
          <ShieldCheck className="w-4 h-4 inline mr-1" /> Conforme al D.lgs 24/2023 · Crittografia AES-128 · Server EU
        </div>
      </div>
    </div>
  );
}
